import {
  createBrowserRouter,
  Navigate,
  Route,
  RouterProvider,
  Routes,
} from "react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import type { ReactNode } from "react";

import { Auth0ProviderWithNavigate } from "@/services/auth0/auth";
import { SessionProvider } from "@/services/auth0/session";
import { SocketProvider } from "@/services/socket/socket-context";
import { useCarrierSocketSubscriptions } from "@/services/socket/use-carrier-socket-subscriptions";
import { DispatchCenterScopeProvider } from "@/services/dispatch-center-scope";
import { GoogleMapsApiProvider } from "@/services/maps/google-maps-api-provider";
import { RequireCarrierCapability } from "@/components/require-carrier-capability";
import { Toaster } from "@/components/ui/sonner";
import { AppLayout } from "@/screens/layout";
import DispatchV2Screen from "@/screens/dispatch-v2/dispatch-v2.screen";
import OrdersScreen from "@/screens/orders.screen";
import TripsScreen from "@/screens/trips.screen";
import TasksScreen from "@/screens/tasks.screen";
import OperatorsScreen from "@/screens/operators.screen";
import VehiclesScreen from "@/screens/vehicles.screen";
import CallbackScreen from "@/screens/callback.screen";
import TeamSettingsScreen from "@/screens/team-settings.screen";
import AcceptInviteScreen from "@/screens/accept-invite.screen";
import ShipmentsScreen from "@/screens/shipments.screen";
import PickupBookingsScreen from "@/screens/pickup-bookings.screen";
import SchedulingScreen from "@/screens/scheduling.screen";
import ApprovalsScreen from "@/screens/approvals.screen";
import CompanySettingsScreen from "@/screens/company-settings.screen";
import DispatchCentersScreen from "@/screens/dispatch-centers.screen";
import DeliverySetupScreen from "@/screens/delivery-setup.screen";
import PricingScreen from "@/screens/pricing.screen";
import ShippingServicesScreen from "@/screens/shipping-services.screen";
import PickupServicesScreen from "@/screens/pickup-services.screen";
import ServiceAreasScreen from "@/screens/service-areas.screen";
import AddOnServicesScreen from "@/screens/add-on-services.screen";
import AnalyticsScreen from "@/screens/analytics.screen";
import CustomerDiscountsScreen from "@/screens/customer-discounts.screen";

const queryClient = new QueryClient();

/**
 * Mounts realtime-event → react-query-invalidation wiring once for the
 * whole app. Must live inside SocketProvider + QueryClientProvider.
 */
function SocketSubscriptionsBridge({ children }: { children: ReactNode }) {
  useCarrierSocketSubscriptions();
  return <>{children}</>;
}

export function AppRoutes() {
  return (
    <Routes>
      <Route path="/callback" element={<CallbackScreen />} />
      <Route path="/accept-invite" element={<AcceptInviteScreen />} />
      <Route path="/accept-invite/:token" element={<AcceptInviteScreen />} />
      <Route
        path="/dispatch"
        element={
          <GoogleMapsApiProvider>
            <DispatchV2Screen />
          </GoogleMapsApiProvider>
        }
      />
      <Route
        path="/dispatch-v2"
        element={<Navigate to="/dispatch" replace />}
      />

      <Route element={<AppLayout />}>
        <Route path="/" element={<Navigate to="/dispatch" replace />} />

        {/* Operations */}
        <Route path="/fulfillment/orders" element={<OrdersScreen />} />
        <Route path="/fulfillment/shipments" element={<ShipmentsScreen />} />
        <Route path="/fulfillment/approvals" element={<ApprovalsScreen />} />
        {/* Old approval-queue routes consolidated into the
                Approvals inbox — redirect stale links to the tab. */}
        <Route
          path="/fulfillment/cancellations"
          element={
            <Navigate to="/fulfillment/approvals?tab=cancellations" replace />
          }
        />
        <Route
          path="/fulfillment/change-requests"
          element={
            <Navigate to="/fulfillment/approvals?tab=change-requests" replace />
          }
        />
        <Route
          path="/pickup-refund-requests"
          element={<Navigate to="/fulfillment/approvals?tab=refunds" replace />}
        />
        <Route path="/team" element={<OperatorsScreen />} />
        <Route
          path="/team/depots"
          element={<Navigate to="/settings/delivery" replace />}
        />
        <Route path="/team/vehicles" element={<VehiclesScreen />} />
        <Route path="/scheduling" element={<SchedulingScreen />} />
        <Route
          path="/pricing"
          element={
            <RequireCarrierCapability capability="canManagePricing">
              <PricingScreen />
            </RequireCarrierCapability>
          }
        />
        <Route
          path="/pricing/discounts"
          element={
            <RequireCarrierCapability capability="canManagePricing">
              <CustomerDiscountsScreen mode="discounts" />
            </RequireCarrierCapability>
          }
        />
        <Route
          path="/pricing/:pricingTab"
          element={
            <RequireCarrierCapability capability="canManagePricing">
              <PricingScreen />
            </RequireCarrierCapability>
          }
        />
        <Route
          path="/services/shipping"
          element={
            <RequireCarrierCapability capability="canManagePricing">
              <ShippingServicesScreen />
            </RequireCarrierCapability>
          }
        />
        <Route
          path="/services/pickup"
          element={
            <RequireCarrierCapability capability="canManagePricing">
              <PickupServicesScreen />
            </RequireCarrierCapability>
          }
        />
        <Route
          path="/services/insurance"
          element={
            <RequireCarrierCapability capability="canManagePricing">
              <AddOnServicesScreen
                typeFilter="INSURANCE"
                title="Insurance Services"
              />
            </RequireCarrierCapability>
          }
        />
        <Route
          path="/services/packaging"
          element={
            <RequireCarrierCapability capability="canManagePricing">
              <AddOnServicesScreen
                typeFilter="PACKAGING"
                title="Packaging Services"
              />
            </RequireCarrierCapability>
          }
        />
        <Route
          path="/services/signature"
          element={
            <RequireCarrierCapability capability="canManagePricing">
              <AddOnServicesScreen
                typeFilter="SIGNATURE"
                title="Signature Services"
              />
            </RequireCarrierCapability>
          }
        />
        <Route
          path="/services/special-handling"
          element={
            <RequireCarrierCapability capability="canManagePricing">
              <AddOnServicesScreen
                typeFilter="SPECIAL_HANDLING"
                title="Special Handling Services"
              />
            </RequireCarrierCapability>
          }
        />
        <Route path="/pickup-bookings" element={<PickupBookingsScreen />} />
        <Route path="/trips" element={<TripsScreen />} />
        <Route path="/trips/tasks" element={<TasksScreen />} />
        <Route path="/analytics" element={<AnalyticsScreen />} />
        <Route
          path="/customers"
          element={<CustomerDiscountsScreen mode="customers" />}
        />

        {/* Platform (read-only substrate) */}
        <Route path="/service-areas" element={<ServiceAreasScreen />} />

        {/* Settings */}
        <Route
          path="/settings/team"
          element={
            <RequireCarrierCapability capability="canManageTeam">
              <TeamSettingsScreen />
            </RequireCarrierCapability>
          }
        />
        <Route
          path="/settings/company"
          element={
            <RequireCarrierCapability capability="canManageCarrierSettings">
              <CompanySettingsScreen />
            </RequireCarrierCapability>
          }
        />
        <Route
          path="/settings/dispatch-centers"
          element={
            <RequireCarrierCapability capability="canManageCarrierSettings">
              <GoogleMapsApiProvider>
                <DispatchCentersScreen />
              </GoogleMapsApiProvider>
            </RequireCarrierCapability>
          }
        />
        <Route
          path="/settings/delivery"
          element={
            <RequireCarrierCapability capability="canManageCarrierSettings">
              <DeliverySetupScreen />
            </RequireCarrierCapability>
          }
        />
      </Route>
    </Routes>
  );
}

function AppRouterRoot() {
  return (
    <Auth0ProviderWithNavigate>
      <SessionProvider>
        <SocketProvider>
          <SocketSubscriptionsBridge>
            <DispatchCenterScopeProvider>
              <AppRoutes />
            </DispatchCenterScopeProvider>
            <Toaster />
          </SocketSubscriptionsBridge>
        </SocketProvider>
      </SessionProvider>
    </Auth0ProviderWithNavigate>
  );
}

/**
 * Created lazily on first render, NOT at module scope. createBrowserRouter()
 * initializes eagerly — it attaches a popstate listener to window as an
 * import side effect. Tests import AppRoutes from this module; a module-level
 * browser router would react to stray popstate events in jsdom (e.g. the
 * async history.back() from useBrowserBackDismiss) after test teardown and
 * crash with a cross-realm AbortSignal TypeError inside undici's Request.
 */
let appRouter: ReturnType<typeof createBrowserRouter> | undefined;

export function Router() {
  appRouter ??= createBrowserRouter([
    {
      path: "*",
      element: <AppRouterRoot />,
    },
  ]);
  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={appRouter} />
    </QueryClientProvider>
  );
}
